// The HCIC booth demo (Kylie's "Freshpaint AI - HCIC Booth Demo" deck) ships a
// dedicated set of canned AI-chat conversations and recommendation cards that
// are scoped to a single customer env. Every other env keeps the existing
// Meridian Health preview-mode content untouched.
export const HCIC_DEMO_PROD_ENV_ID = "e7900f97-f9a7-46c6-ae25-6ab8db454a6a";

const HCIC_DEMO_ENV_IDS = new Set<string>([HCIC_DEMO_PROD_ENV_ID]);

export function isHcicDemoEnv(envId: string | null | undefined): boolean {
  return !!envId && HCIC_DEMO_ENV_IDS.has(envId);
}

// The preview-mode REST handlers (sidebar list, recommendation cards) run
// outside React and never receive the env in their request, so they resolve the
// selected env through this module. The source of truth is the Redux store,
// which holds the resolved env even when it was defaulted rather than explicitly
// picked (the env locker only writes localStorage on an explicit switch). The
// store registers its getter at startup via setEnvIdResolver — kept as an
// injected callback so this module imports nothing and can't form a load-time
// cycle with the root reducer.
let envIdResolver: (() => string | null) | null = null;

export function setEnvIdResolver(resolver: () => string | null): void {
  envIdResolver = resolver;
}

// The env locker persists an explicitly-selected env id here, used as a fallback
// when the resolver isn't wired (e.g. unit tests).
const CURRENT_ENV_STORAGE_KEY = "currentEnvironment";

function envIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(CURRENT_ENV_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "string") return parsed;
    if (parsed && typeof parsed === "object" && "ID" in parsed) {
      const id = (parsed as { ID?: unknown }).ID;
      return typeof id === "string" ? id : null;
    }
    return null;
  } catch {
    return null;
  }
}

// Returns the resolved current env id, or null before an env is known (which
// maps to the default Meridian demo profile).
export function getCurrentEnvId(): string | null {
  return envIdResolver?.() ?? envIdFromStorage();
}

export function isCurrentEnvHcicDemo(): boolean {
  return isHcicDemoEnv(getCurrentEnvId());
}
